import { useEffect, useId, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

interface ModalProps {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  size?: "sm" | "md" | "lg";
}

const WIDTH = { sm: "max-w-sm", md: "max-w-lg", lg: "max-w-3xl" } as const;

export function Modal({ open, title, onClose, children, footer, size = "md" }: ModalProps) {
  const { t } = useTranslation();
  const titleId = useId();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-cosmic-navy/60 p-4 backdrop-blur-sm" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div role="dialog" aria-modal="true" aria-labelledby={titleId} className={`flex max-h-[90vh] w-full flex-col overflow-hidden rounded-3xl bg-white shadow-2xl ${WIDTH[size]}`}>
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3.5">
          <h2 id={titleId} className="text-sm font-black uppercase tracking-wide text-cosmic-navy">{title}</h2>
          <button type="button" onClick={onClose} aria-label={t("common.close")} className="rounded-lg px-2 py-1 text-lg leading-none text-gray-400 hover:bg-gray-100 hover:text-gray-700">×</button>
        </div>
        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">{children}</div>
        {footer && <div className="flex items-center justify-end gap-2 border-t border-gray-100 bg-gray-50 px-5 py-3">{footer}</div>}
      </div>
    </div>
  );
}
